/**
 * `surveyQuestionnaireEditOf` — the survey question editor's one validator (plan-030 DOM-Q; design §41.6; glossary
 * §6.45; ADR 0084, proposed; DD30-2).
 *
 * A save of the portal's Questions view, its check, and the factory's questionnaire carry and verbatim JSON upload
 * (design §41.7) all call this one pure function, so "valid" has one definition. It takes the editable version's
 * stored extraction (`base`), the questionnaire document the owner sends (`next`, untrusted), the VoiceAgent's question
 * code history, and the language pack's phrases for the words wetalk adds; it answers every problem at once, each with
 * a stable code and its JSON path (`survey.el.variants.A.questions.3.options`), the warnings that never block, and —
 * when there is no problem — the extraction to store, a change summary of codes only (the audit row's detail, never
 * wording), and whether anything changed at all.
 *
 * THE PIPELINE (design §41.6.1), in this order, per language file:
 *
 * 1. Shape. Every language the base has, and no other; one object per language, written as `wetalk-survey-json-v2`.
 *    A file carrying a Release 2 key (`skip_rules`, `mandatory_lines`, `forbidden_phrases`) is refused
 *    `survey_rule_unavailable`: Release 1 has no v3 reader anywhere (plan-030 DOM-R adds the v3 branch, behind
 *    `ruleAvailable`). A v1 file is refused `questionnaire_format_old`: the editor always sends the current format,
 *    moving a v1 questionnaire with {@link surveyFileV2Of} (§41.6.6). Adding or removing a group stays an Edit with a
 *    new upload (FR-SQE-10).
 * 2. Limits (OD-9, `survey-limit.ts`), counted on the raw input before any parsing work grows with it.
 * 3. The words wetalk adds (OD-7, §41.6.1 step 8) — run before the strict parse, because the strict schema requires
 *    each of them: a new choice, open or number question's clarification, a several-picks question's follow-up, the
 *    ordinals the added questions need, and the count word for a changed count. Each is filled from the pack's phrase
 *    for the file's language and warned `wording_added` (the portal marks it "check the wording"); a language without
 *    the phrases gets `wording_missing`, naming the field. Nothing is ever borrowed from another language. Only what an
 *    edit made necessary is filled: an existing question's emptied clarification, or a group with one ordinal too few
 *    and no question added, is a problem (AC-4).
 * 4. The existing rules, unchanged — the strict v2 schema and `surveyQuestionnaireOf`'s checks, each issue mapped to its
 *    code: ids unique per group, a question in both groups the same question, choice limits, number ranges, every
 *    objection answered (only `hostile` silent), one ordinal per numbered question, a count word for `{count}`, the
 *    language contract.
 * 5. Codes (DD30-6). A code the history holds with another type is refused `question_code_type_changed`, naming a
 *    `free_code`; a new question taking a history code of the same type is the same question coming back, allowed
 *    and warned `question_code_reused`.
 * 6. Unchanged. Deep-equal to the base's files after filling → `changed: false` and the base extraction itself.
 *
 * Release 2's steps 5–7 of the design (skip rules, mandatory lines, forbidden phrases) belong to plan-030 DOM-R and
 * read `platformLine` and `ruleAvailable`; Release 1 accepts both and refuses every v3 key. The prefix measurement
 * (§41.6.1 step 9) is the api's, after this pure step. As a last guard, a result is never `ok` unless
 * `surveyQuestionnaireOf(…, "v2")` and `surveyExtractionOf` — the readers every build and every interviewer use —
 * accept it too.
 *
 * Pure: no clock, no network, no language pack import (the phrases arrive through {@link SurveyPackPhrase}).
 */
import { SURVEY_FILE_V1, SURVEY_FILE_V2, SURVEY_OBJECTION, SURVEY_VARIANT, SurveyFileV2Schema, isSurveyFileV2Shaped, numberedCountOf, surveyArmOf, surveyExtractionOf, surveyQuestionnaireOf, surveyVariantsOf, } from "./questionnaire.js";
import { spokenNumberOf } from "./spoken-number.js";
import { SURVEY_OPTION_LIMIT, SURVEY_QUESTION_LIMIT, SURVEY_TEXT_LIMIT } from "./survey-limit.js";
// ---- codes ----------------------------------------------------------------------------------------------------------
/**
 * Every problem code of design §41.6.3, Release 2's included so the api and the portal register one closed set. Release
 * 1 never answers `rule_*`, `mandatory_line_*`, `forbidden_phrase_*` (plan-030 DOM-R) or `prefix_unmeasurable` (the
 * api's, after this pure step).
 */
export const QUESTIONNAIRE_PROBLEM_CODE = [
    "questionnaire_shape",
    "questionnaire_format_old",
    "survey_rule_unavailable",
    "question_limit_exceeded",
    "option_limit_exceeded",
    "rule_limit_exceeded",
    "mandatory_line_limit_exceeded",
    "forbidden_phrase_limit_exceeded",
    "text_too_long",
    "question_id_duplicate",
    "question_differs_between_groups",
    "choice_selection_invalid",
    "choice_exclusive_invalid",
    "choice_more_missing",
    "number_range_invalid",
    "objection_reply_missing",
    "ordinal_missing",
    "count_word_missing",
    "language_contract",
    "question_code_type_changed",
    "rule_condition_not_earlier",
    "rule_target_not_later",
    "rule_target_is_condition",
    "rule_answer_invalid",
    "rule_range_invalid",
    "rule_condition_form",
    "rule_count_in_intro",
    "mandatory_line_position_unknown",
    "mandatory_line_position_form",
    "forbidden_phrase_in_line",
    "forbidden_phrase_in_platform_line",
    "wording_missing",
    "prefix_unmeasurable",
];
/** Every warning code of design §41.6.2 (never blocks). `mandatory_line_may_be_skipped` and `rule_skips_to_end` are Release 2's. */
export const QUESTIONNAIRE_WARNING_CODE = [
    "question_reworded",
    "option_removed",
    "question_moved",
    "how_long_mentions_number",
    "wording_added",
    "question_code_reused",
    "mandatory_line_may_be_skipped",
    "rule_skips_to_end",
    "format_converted",
];
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isBlank(value) {
    return value === undefined || (typeof value === "string" && value.trim() === "");
}
/** A deep copy of JSON data (the body is parsed JSON; a stored extraction is JSON). */
function copyOf(value) {
    return JSON.parse(JSON.stringify(value));
}
/** Deep equality of JSON data: object keys in any order, arrays in order. */
export function sameQuestionnaireJson(a, b) {
    if (a === b)
        return true;
    if (Array.isArray(a) || Array.isArray(b)) {
        if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length)
            return false;
        return a.every((one, index) => sameQuestionnaireJson(one, b[index]));
    }
    if (isRecord(a) && isRecord(b)) {
        const keyA = Object.keys(a).filter((key) => a[key] !== undefined);
        const keyB = Object.keys(b).filter((key) => b[key] !== undefined);
        if (keyA.length !== keyB.length)
            return false;
        return keyA.every((key) => Object.hasOwn(b, key) && sameQuestionnaireJson(a[key], b[key]));
    }
    return false;
}
/** The type a history code holds: its own, or its kind's (`statement` is a scale question; v1's two are choices). */
function historyTypeOf(entry) {
    if (entry.questionType !== null)
        return entry.questionType;
    switch (entry.questionKind) {
        case "statement":
            return "scale";
        case "emotion":
        case "source":
        case "choice":
            return "choice";
        case "open":
            return "open";
        case "number":
            return "number";
    }
}
/** `Q` and the smallest number such that the code is taken by nothing given (design §41.6.4). */
export function freeQuestionCodeOf(taken) {
    const used = new Set(taken);
    for (let number = 1;; number += 1) {
        const code = `Q${String(number)}`;
        if (!used.has(code))
            return code;
    }
}
/** Every question id readable in a raw survey map, however malformed the rest of it is. */
function rawQuestionIdOf(survey) {
    if (!isRecord(survey))
        return [];
    const out = [];
    for (const file of Object.values(survey)) {
        if (!isRecord(file) || !isRecord(file.variants))
            continue;
        for (const arm of Object.values(file.variants)) {
            if (!isRecord(arm) || !Array.isArray(arm.questions))
                continue;
            for (const question of arm.questions)
                if (isRecord(question) && typeof question.id === "string")
                    out.push(question.id);
        }
    }
    return out;
}
function documentQuestionIdOf(survey) {
    return surveyVariantsOf(survey).flatMap((variant) => surveyArmOf(survey, variant).questions.map((question) => question.id));
}
/** Indefinite articles that number tables read as "one" ("a few minutes", «μια σύντομη έρευνα») — not a stated length. */
const ARTICLE = new Set(["a", "an", "μια", "ενα", "εναν", "un", "una", "uno", "um", "uma"]);
/** Whether a reply states a number: a digit, or a number word the language's table reads (design §41.6.2). */
function mentionsNumber(text, language) {
    if (/\p{Nd}/u.test(text))
        return true;
    return text.split(/[^\p{L}]+/u).some((word) => {
        const plain = word.toLowerCase().normalize("NFD").replace(/\p{M}/gu, "");
        return plain.length > 1 && !ARTICLE.has(plain) && spokenNumberOf(word, language) !== null;
    });
}
/** The same rule as the questionnaire's: a question asked in two groups or two languages is the same question. */
function questionContractOf(question) {
    switch (question.type) {
        case "choice":
            return JSON.stringify({
                type: question.type, options: question.options.map((option) => option.id),
                min: question.selection?.min, max: question.selection?.max, exclusive: question.selection?.exclusive,
            });
        case "number":
            return JSON.stringify({ type: question.type, min: question.min, max: question.max, integer: question.integer });
        default:
            return JSON.stringify({ type: question.type });
    }
}
function languageContractOf(survey) {
    return JSON.stringify(surveyVariantsOf(survey).map((variant) => surveyArmOf(survey, variant).questions.map((question) => ({ id: question.id, contract: questionContractOf(question) }))));
}
const RELEASE_2_GROUP_KEY = ["skip_rules", "mandatory_lines"];
const RELEASE_2_FILE_KEY = ["forbidden_phrases"];
/** A question's own spoken strings, bounded by {@link SURVEY_TEXT_LIMIT}. */
const QUESTION_TEXT_KEY = ["text", "clarify", "suffix"];
function problemAt(context, code, path, message, detail) {
    context.out.problem.push({ code, path: path === "" ? context.prefix : `${context.prefix}.${path}`, message, ...(detail === undefined ? {} : { detail }) });
}
function warningAt(context, code, path, message, detail) {
    context.out.warning.push({ code, path: path === "" ? context.prefix : `${context.prefix}.${path}`, message, ...(detail === undefined ? {} : { detail }) });
}
function wordingMissing(context, path, field, detail = {}) {
    problemAt(context, "wording_missing", path, `wetalk has no ${field.replace("_", " ")} wording for this language: type it yourself.`, { field, language: context.language, ...detail });
}
function wordingAdded(context, path, field, detail = {}) {
    warningAt(context, "wording_added", path, `wetalk added this ${field.replace("_", " ")}: check the wording.`, { field, ...detail });
}
/** Steps 1–2: refuses what Release 1 cannot read, and counts sizes before any parsing work. `true` when it may go on. */
function shapeAndLimitPass(context, raw) {
    if (!isRecord(raw)) {
        problemAt(context, "questionnaire_shape", "", "Each language's questionnaire must be an object.");
        return false;
    }
    let release2 = false;
    for (const key of RELEASE_2_FILE_KEY) {
        if (Object.hasOwn(raw, key)) {
            release2 = true;
            problemAt(context, "survey_rule_unavailable", key, "Rules (skip rules, mandatory lines, forbidden phrases) are not available yet.");
        }
    }
    if (isRecord(raw.variants)) {
        for (const [variant, arm] of Object.entries(raw.variants)) {
            if (!isRecord(arm))
                continue;
            for (const key of RELEASE_2_GROUP_KEY) {
                if (Object.hasOwn(arm, key)) {
                    release2 = true;
                    problemAt(context, "survey_rule_unavailable", `variants.${variant}.${key}`, "Rules (skip rules, mandatory lines, forbidden phrases) are not available yet.");
                }
            }
        }
    }
    if (release2)
        return false;
    if (!isSurveyFileV2Shaped(raw)) {
        const arm = isRecord(raw.variants) ? raw.variants["A"] : undefined;
        if (isRecord(arm) && "statements" in arm) {
            problemAt(context, "questionnaire_format_old", "variants", "This questionnaire is in the first format (statements, then the feelings and news questions). Save it in the " +
                "current format: each group holds \"questions\", each with a type.");
        }
        else {
            problemAt(context, "questionnaire_shape", "variants", "Each group must hold \"questions\".");
        }
        return false;
    }
    const variants = raw.variants;
    let bounded = true;
    for (const [variant, arm] of Object.entries(variants)) {
        if (!isRecord(arm) || !Array.isArray(arm.questions))
            continue;
        if (arm.questions.length > SURVEY_QUESTION_LIMIT) {
            bounded = false;
            problemAt(context, "question_limit_exceeded", `variants.${variant}.questions`, `A group may hold at most ${String(SURVEY_QUESTION_LIMIT)} questions; this one holds ${String(arm.questions.length)}.`, { limit: SURVEY_QUESTION_LIMIT, count: arm.questions.length });
            continue;
        }
        for (const [index, question] of arm.questions.entries()) {
            if (!isRecord(question))
                continue;
            const at = `variants.${variant}.questions.${String(index)}`;
            if (Array.isArray(question.options) && question.options.length > SURVEY_OPTION_LIMIT) {
                bounded = false;
                problemAt(context, "option_limit_exceeded", `${at}.options`, `A choice question may offer at most ${String(SURVEY_OPTION_LIMIT)} options; this one offers ${String(question.options.length)}.`, { limit: SURVEY_OPTION_LIMIT, count: question.options.length });
                continue;
            }
            const long = (path, value) => {
                if (typeof value === "string" && value.length > SURVEY_TEXT_LIMIT) {
                    bounded = false;
                    problemAt(context, "text_too_long", path, `A spoken line may be at most ${String(SURVEY_TEXT_LIMIT)} characters; this one has ${String(value.length)}.`, { limit: SURVEY_TEXT_LIMIT, length: value.length });
                }
            };
            for (const key of QUESTION_TEXT_KEY)
                long(`${at}.${key}`, question[key]);
            if (isRecord(question.selection))
                long(`${at}.selection.more`, question.selection.more);
            if (Array.isArray(question.options)) {
                for (const [optionIndex, option] of question.options.entries()) {
                    if (isRecord(option))
                        long(`${at}.options.${String(optionIndex)}.label`, option.label);
                }
            }
        }
    }
    const groupNext = SURVEY_VARIANT.filter((variant) => Object.hasOwn(variants, variant));
    const groupBase = surveyVariantsOf(context.baseSurvey);
    if (groupNext.join() !== groupBase.join()) {
        problemAt(context, "questionnaire_shape", "variants", `This questionnaire has ${String(groupBase.length)} group(s). Adding or removing a group needs a new questionnaire file in Edit.`, { group_count: groupBase.length });
        return false;
    }
    return bounded;
}
/** Step 3 (design step 8): the words an edit made necessary, filled from the pack in place. */
function fillWording(context, file) {
    const phrase = context.packPhrase;
    const baseById = new Map();
    for (const variant of surveyVariantsOf(context.baseSurvey)) {
        for (const question of surveyArmOf(context.baseSurvey, variant).questions)
            baseById.set(question.id, question);
    }
    const variants = file.variants;
    const scale = isRecord(file.scale) ? file.scale : null;
    const sharedOrdinal = scale !== null && Array.isArray(scale.ordinals) ? [...scale.ordinals] : null;
    const adjusted = new Map();
    const countByVariant = new Map();
    for (const variant of SURVEY_VARIANT) {
        const arm = variants[variant];
        if (!isRecord(arm) || !Array.isArray(arm.questions))
            continue;
        const questions = arm.questions;
        for (const [index, question] of questions.entries()) {
            if (!isRecord(question))
                continue;
            const at = `variants.${variant}.questions.${String(index)}`;
            const code = typeof question.id === "string" ? question.id : null;
            const held = code === null ? undefined : baseById.get(code);
            // New to this edit: a code the base does not ask, or one whose type changed (allowed only for a code no
            // published version asked — the code check refuses the rest).
            const fresh = held === undefined || held.type !== question.type;
            const clarify = question.type === "choice" ? phrase?.clarifyChoice
                : question.type === "open" ? phrase?.clarifyOpen
                    : question.type === "number" ? phrase?.clarifyNumber : undefined;
            if (question.type !== "scale" && typeof question.type === "string" && fresh && isBlank(question.clarify)) {
                if (phrase === null)
                    wordingMissing(context, `${at}.clarify`, "clarify", { question_code: code });
                else if (clarify !== undefined) {
                    question.clarify = clarify;
                    wordingAdded(context, `${at}.clarify`, "clarify", { question_code: code });
                }
            }
            const selection = question.selection;
            if (question.type === "choice" && isRecord(selection) && typeof selection.max === "number" && selection.max > 1 && isBlank(selection.more)) {
                // A follow-up the owner removed from a question that already had several picks is theirs to restore.
                const heldMax = held !== undefined && held.type === "choice" && held.selection !== undefined ? held.selection.max : 1;
                if (fresh || heldMax <= 1) {
                    if (phrase === null)
                        wordingMissing(context, `${at}.selection.more`, "more", { question_code: code });
                    else {
                        selection.more = phrase.choiceMore;
                        wordingAdded(context, `${at}.selection.more`, "more", { question_code: code });
                    }
                }
            }
        }
        // Ordinals are positional: the added questions' positions take the pack's ordinal with the position as digits, and
        // the last position the pack's last ordinal; removed questions drop positions, keeping the file's own last ordinal.
        const count = questions.length;
        countByVariant.set(variant, count);
        const own = Array.isArray(arm.ordinals);
        const ordinal = own ? arm.ordinals : sharedOrdinal;
        const baseArm = context.baseSurvey.variants[variant];
        if (ordinal === null || baseArm === undefined || ordinal.length === 0 || count === 0)
            continue;
        const baseCount = numberedCountOf(baseArm);
        const path = own ? `variants.${variant}.ordinals` : "scale.ordinals";
        if (ordinal.length < count && count - ordinal.length <= Math.max(0, count - baseCount)) {
            if (phrase === null) {
                wordingMissing(context, path, "ordinal", { variant, count });
                continue;
            }
            const added = [];
            for (let position = ordinal.length - 1; position < count - 1; position += 1) {
                added.push(phrase.questionOrdinal.replaceAll("{number}", String(position + 1)));
            }
            adjusted.set(variant, { ordinal: [...ordinal.slice(0, ordinal.length - 1), ...added, phrase.lastQuestionOrdinal], own });
            wordingAdded(context, path, "ordinal", { variant, count });
        }
        else if (ordinal.length > count && ordinal.length - count <= Math.max(0, baseCount - count)) {
            adjusted.set(variant, { ordinal: [...ordinal.slice(0, count - 1), ordinal[ordinal.length - 1]], own });
        }
    }
    // Write the adjusted ordinals back where each group reads them: its own, or the shared `scale.ordinals`. When two
    // groups read the shared ones and end up different, the first keeps the shared list and the other takes its own.
    for (const [variant, one] of adjusted) {
        if (one.own)
            variants[variant].ordinals = one.ordinal;
    }
    const sharedReader = SURVEY_VARIANT.filter((variant) => {
        const arm = variants[variant];
        return isRecord(arm) && !Array.isArray(arm.ordinals) && !adjusted.get(variant)?.own;
    });
    if (scale !== null && sharedOrdinal !== null && sharedReader.some((variant) => adjusted.has(variant))) {
        const finalOf = (variant) => adjusted.get(variant)?.ordinal ?? sharedOrdinal;
        const [first, ...rest] = sharedReader;
        scale.ordinals = [...finalOf(first)];
        for (const variant of rest) {
            if (!sameQuestionnaireJson(finalOf(variant), finalOf(first)))
                variants[variant].ordinals = [...finalOf(variant)];
        }
    }
    // The count word for a changed count, when the explanation speaks `{count}`: the number as digits.
    if (scale !== null && typeof scale.intro === "string" && scale.intro.includes("{count}") && Array.isArray(file.count_words)) {
        const words = file.count_words;
        for (const [variant, count] of countByVariant) {
            const baseArm = context.baseSurvey.variants[variant];
            const word = words[count];
            if (baseArm === undefined || !isBlank(word) || count === numberedCountOf(baseArm))
                continue;
            if (word !== undefined && typeof word !== "string")
                continue;
            if (phrase === null) {
                wordingMissing(context, "count_words", "count_word", { variant, count });
                continue;
            }
            while (words.length <= count)
                words.push("");
            words[count] = String(count);
            wordingAdded(context, `count_words.${String(count)}`, "count_word", { variant, count });
        }
    }
}
/**
 * Step 4, the arm checks: one ordinal per numbered question, and a count word when `{count}` is spoken. Read on the
 * filled file before the strict parse, so they are listed beside the schema's problems rather than after them (AC-4).
 * Every v2 question is numbered.
 */
function armCountPass(context, file, reported) {
    const variants = file.variants;
    const scale = isRecord(file.scale) ? file.scale : null;
    const words = file.count_words;
    for (const variant of SURVEY_VARIANT) {
        const arm = variants[variant];
        if (!isRecord(arm) || !Array.isArray(arm.questions))
            continue;
        const count = arm.questions.length;
        const own = Array.isArray(arm.ordinals);
        const ordinal = own ? arm.ordinals : scale !== null && Array.isArray(scale.ordinals) ? scale.ordinals : null;
        const ordinalPath = own ? `variants.${variant}.ordinals` : "scale.ordinals";
        if (ordinal !== null && ordinal.length !== count && !reported.has(`${context.prefix}.${ordinalPath}`)) {
            problemAt(context, "ordinal_missing", ordinalPath, `Group ${variant} has ${String(count)} questions and needs exactly that many ordinals, the last announcing the last question; it has ${String(ordinal.length)}.`, { variant, count, ordinal_count: ordinal.length });
        }
        if (scale !== null && typeof scale.intro === "string" && scale.intro.includes("{count}") && Array.isArray(words)
            && isBlank(words[count]) && !reported.has(`${context.prefix}.count_words`)) {
            problemAt(context, "count_word_missing", "count_words", `The explanation says {count}: count_words needs a word at position ${String(count)} for group ${variant}.`, { variant, count });
        }
    }
}
/** Step 4a: a strict-schema issue, mapped to its code. */
function problemOfIssue(context, issue, reported) {
    const path = issue.path.map(String).join(".");
    // A word wetalk could not add is already named `wording_missing` at this path.
    if (reported.has(path === "" ? context.prefix : `${context.prefix}.${path}`))
        return;
    const last = issue.path.length === 0 ? undefined : String(issue.path[issue.path.length - 1]);
    const place = path === "" ? "(the file)" : path;
    if (issue.path[0] === "objections") {
        problemAt(context, "objection_reply_missing", path, `Every reply must be written (only "hostile" may be silent): ${place}: ${issue.message}`);
        return;
    }
    if (issue.code === "custom") {
        if (last === "exclusive")
            return problemAt(context, "choice_exclusive_invalid", path, issue.message);
        if (last === "more")
            return problemAt(context, "choice_more_missing", path, issue.message);
        if (last === "selection")
            return problemAt(context, "choice_selection_invalid", path, issue.message);
        if (last === "min")
            return problemAt(context, "number_range_invalid", path, issue.message);
    }
    problemAt(context, "questionnaire_shape", path, `${place}: ${issue.message}`);
}
/** Step 4b: the questionnaire's own checks, every one of them (the reader stops at the first). */
function documentPass(context, survey, file) {
    if (file.language !== context.language) {
        problemAt(context, "questionnaire_shape", "language", `This file must be the "${context.language}" questionnaire.`);
    }
    const byId = new Map();
    for (const variant of surveyVariantsOf(survey)) {
        const arm = surveyArmOf(survey, variant);
        const inArm = new Set();
        for (const [index, question] of arm.questions.entries()) {
            const at = `variants.${variant}.questions.${String(index)}.id`;
            if (inArm.has(question.id)) {
                problemAt(context, "question_id_duplicate", at, `Code ${question.id} appears twice in group ${variant}. Each question needs its own code.`, { question_code: question.id });
            }
            inArm.add(question.id);
            const held = byId.get(question.id);
            if (held !== undefined && questionContractOf(held) !== questionContractOf(question)) {
                problemAt(context, "question_differs_between_groups", at, `Code ${question.id} is a different question in each group. A question asked in both groups must be the same question; give different questions different codes.`, { question_code: question.id });
            }
            byId.set(question.id, question);
        }
    }
    for (const type of SURVEY_OBJECTION) {
        const reply = file.objections[type];
        if (type !== "hostile" && reply.answer.trim() === "") {
            problemAt(context, "objection_reply_missing", `objections.${type}.answer`, `The reply to "${type}" is empty. Only "hostile" may be silent.`, { objection: type });
        }
    }
}
/** Step 5: codes keep their type (DD30-6). */
function codePass(context, survey) {
    const baseId = new Set(documentQuestionIdOf(context.baseSurvey));
    const seen = new Set();
    for (const variant of surveyVariantsOf(survey)) {
        for (const [index, question] of surveyArmOf(survey, variant).questions.entries()) {
            if (seen.has(question.id))
                continue;
            seen.add(question.id);
            const entry = context.history.get(question.id);
            if (entry === undefined)
                continue;
            const at = `variants.${variant}.questions.${String(index)}.id`;
            const was = historyTypeOf(entry);
            const label = entry.firstAgentVersionLabel;
            if (was !== question.type) {
                problemAt(context, "question_code_type_changed", at, `Code ${question.id} was a ${was} question${label === null ? "" : ` in ${label}`}; a code keeps its type so its results stay together. ` +
                    `Give this ${question.type} question a new code, such as ${context.freeCode}.`, { question_code: question.id, history_type: was, question_type: question.type, free_code: context.freeCode });
            }
            else if (!baseId.has(question.id)) {
                warningAt(context, "question_code_reused", at, `Earlier answers to ${question.id} will be shown with this question.`, { question_code: question.id, first_agent_version_label: label });
            }
        }
    }
}
/** The warnings against the base (design §41.6.2). */
function changePass(context, survey) {
    const base = context.baseSurvey;
    const baseById = new Map();
    for (const variant of surveyVariantsOf(base))
        for (const question of surveyArmOf(base, variant).questions)
            baseById.set(question.id, question);
    const seen = new Set();
    for (const variant of surveyVariantsOf(survey)) {
        const arm = surveyArmOf(survey, variant);
        for (const [index, question] of arm.questions.entries()) {
            const held = baseById.get(question.id);
            if (held === undefined || seen.has(question.id))
                continue;
            seen.add(question.id);
            const at = `variants.${variant}.questions.${String(index)}`;
            if (held.text !== question.text) {
                warningAt(context, "question_reworded", `${at}.text`, `${question.id}'s wording changed. Its earlier answers stay together with the new wording. Changing the meaning? Add a new question instead.`, { question_code: question.id });
            }
            if (held.type === "choice" && question.type === "choice") {
                const kept = new Set(question.options.map((option) => option.id));
                for (const option of held.options) {
                    if (kept.has(option.id))
                        continue;
                    warningAt(context, "option_removed", `${at}.options`, `Option ${option.id} was removed from ${question.id}. Earlier answers keep its label in results.`, { question_code: question.id, option_id: option.id });
                }
            }
        }
        const baseArm = base.variants[variant];
        if (baseArm === undefined)
            continue;
        const nextId = new Set(arm.questions.map((question) => question.id));
        const baseId = new Set(baseArm.questions.map((question) => question.id));
        const was = baseArm.questions.map((question) => question.id).filter((id) => nextId.has(id));
        const now = arm.questions.map((question) => question.id).filter((id) => baseId.has(id));
        for (const [position, id] of now.entries()) {
            if (was[position] === id)
                continue;
            const index = arm.questions.findIndex((question) => question.id === id);
            warningAt(context, "question_moved", `variants.${variant}.questions.${String(index)}`, `${id} was moved.`, { question_code: id });
        }
    }
    const countChanged = surveyVariantsOf(survey).some((variant) => {
        const baseArm = base.variants[variant];
        return baseArm !== undefined && numberedCountOf(baseArm) !== numberedCountOf(surveyArmOf(survey, variant));
    });
    const howLong = survey.objections.how_long;
    if (countChanged && howLong !== undefined && mentionsNumber(howLong.answer, context.language)) {
        warningAt(context, "how_long_mentions_number", "objections.how_long.answer", "The number of questions changed, and the reply to \"How long will it take?\" mentions a number. Check it is still right.");
    }
}
function languagePass(context, raw) {
    if (!shapeAndLimitPass(context, raw)) {
        return { filled: isRecord(raw) ? raw : null, parsed: null, survey: null };
    }
    const file = copyOf(raw);
    const before = context.out.problem.length;
    fillWording(context, file);
    const reported = new Set(context.out.problem.slice(before).map((one) => one.path));
    armCountPass(context, file, reported);
    const parsed = SurveyFileV2Schema.safeParse(file);
    if (!parsed.success) {
        for (const issue of parsed.error.issues)
            problemOfIssue(context, issue, reported);
        return { filled: file, parsed: null, survey: null };
    }
    // The model read as a stored v2 extraction is read (the schema alone), so the checks below name every problem the
    // questionnaire's reader would stop at the first of.
    const survey = surveyExtractionOf({ referenceSchema: SURVEY_FILE_V2, survey: { [context.language]: parsed.data } })?.survey[context.language];
    if (survey === undefined) {
        problemAt(context, "questionnaire_shape", "", "The questionnaire could not be read.");
        return { filled: parsed.data, parsed: null, survey: null };
    }
    const beforeDocument = context.out.problem.length;
    documentPass(context, survey, parsed.data);
    codePass(context, survey);
    changePass(context, survey);
    const valid = context.out.problem.length === beforeDocument;
    return { filled: parsed.data, parsed: valid ? parsed.data : null, survey: valid ? survey : null };
}
// ---- the validator ------------------------------------------------------------------------------------------------------
/** Reads a stored base, which was valid when it was written: anything else is a platform fault, thrown. */
function baseQuestionnaireOf(base) {
    const read = surveyExtractionOf(base);
    if (read === null)
        throw new Error("The editable version's extraction is not a questionnaire.");
    return read.survey;
}
/**
 * The survey question editor's validator (design §41.6). Pure; never throws on the owner's input (a base that is not a
 * questionnaire throws: it is the caller's fault, never the owner's).
 */
export function surveyQuestionnaireEditOf(input) {
    const baseSurvey = baseQuestionnaireOf(input.base);
    const baseLanguage = Object.keys(input.base.survey);
    const history = new Map(input.history.map((entry) => [entry.questionCode, entry]));
    const freeCode = freeQuestionCodeOf([
        ...history.keys(),
        ...Object.values(baseSurvey).flatMap(documentQuestionIdOf),
        ...rawQuestionIdOf(input.next),
    ]);
    const out = { problem: [], warning: [] };
    if (input.base.referenceSchema === SURVEY_FILE_V1) {
        out.warning.push({
            code: "format_converted", path: "survey",
            message: "This questionnaire is saved in the current format: the feelings and news questions are now numbered like the others.",
        });
    }
    const next = input.next;
    if (!isRecord(next)) {
        out.problem.push({ code: "questionnaire_shape", path: "survey", message: "The questionnaire must be an object of one file per language." });
        return { ok: false, problem: out.problem, warning: out.warning, survey: null, freeCode };
    }
    const nextLanguage = Object.keys(next);
    if (nextLanguage.length !== baseLanguage.length || nextLanguage.some((language) => !baseLanguage.includes(language))) {
        out.problem.push({
            code: "questionnaire_shape", path: "survey",
            message: `The questionnaire must hold exactly this version's languages: ${baseLanguage.join(", ")}.`,
            detail: { language: baseLanguage },
        });
        return { ok: false, problem: out.problem, warning: out.warning, survey: null, freeCode };
    }
    const filled = {};
    const parsed = {};
    const read = {};
    for (const language of baseLanguage) {
        const baseOne = baseSurvey[language];
        const context = {
            language, prefix: `survey.${language}`, baseSurvey: baseOne,
            packPhrase: Object.hasOwn(input.packPhrase, language) ? input.packPhrase[language] : null,
            history, freeCode, out,
        };
        const outcome = languagePass(context, next[language]);
        filled[language] = outcome.filled;
        if (outcome.parsed !== null && outcome.survey !== null) {
            parsed[language] = outcome.parsed;
            read[language] = outcome.survey;
        }
    }
    // The language contract: every language asks the same questions (ids, types, order, options, limits, ranges).
    const contractLanguage = Object.keys(read);
    const firstContract = contractLanguage[0] === undefined ? null : languageContractOf(read[contractLanguage[0]]);
    for (const language of contractLanguage.slice(1)) {
        if (languageContractOf(read[language]) !== firstContract) {
            out.problem.push({
                code: "language_contract", path: `survey.${language}.variants`,
                message: "Every language must ask the same questions: the same codes, types and order, and the same options, limits and ranges.",
            });
        }
    }
    if (out.problem.length === 0) {
        // The last guard: the readers every build and every interviewer use must accept exactly what is stored.
        const upload = baseLanguage.map((language) => ({ file: `survey.${language}`, text: JSON.stringify(parsed[language]) }));
        const accepted = surveyQuestionnaireOf(upload, "v2");
        if (!accepted.ok) {
            const key = accepted.location.key;
            out.problem.push({ code: "questionnaire_shape", path: key === undefined ? accepted.location.file : `${accepted.location.file}.${key}`, message: accepted.problem });
        }
        else if (surveyExtractionOf(accepted.extraction) === null) {
            out.problem.push({ code: "questionnaire_shape", path: "survey", message: "The questionnaire could not be read back." });
        }
    }
    if (out.problem.length > 0)
        return { ok: false, problem: out.problem, warning: out.warning, survey: filled, freeCode };
    const changed = input.base.referenceSchema !== SURVEY_FILE_V2 || !sameQuestionnaireJson(parsed, input.base.survey);
    const extraction = changed ? { referenceSchema: SURVEY_FILE_V2, survey: parsed } : input.base;
    return {
        ok: true, extraction, summary: summaryOf(baseSurvey, read, baseLanguage, out.warning), warning: out.warning, changed, freeCode,
    };
}
function summaryOf(base, next, language, warning) {
    const first = next[language[0]];
    const firstBase = base[language[0]];
    const nextId = [...new Set(documentQuestionIdOf(first))];
    const baseId = [...new Set(documentQuestionIdOf(firstBase))];
    const reworded = new Set();
    for (const one of warning) {
        const code = one.detail?.["question_code"];
        if (one.code === "question_reworded" && typeof code === "string")
            reworded.add(code);
    }
    const armB = first.variants.B;
    return {
        format: SURVEY_FILE_V2,
        questionCount: { A: first.variants.A.questions.length, ...(armB === undefined ? {} : { B: armB.questions.length }) },
        added: nextId.filter((id) => !baseId.includes(id)),
        removed: baseId.filter((id) => !nextId.includes(id)),
        reworded: [...reworded],
        moved: warning.some((one) => one.code === "question_moved"),
        ruleCount: 0,
        mandatoryLineCount: 0,
        forbiddenPhraseCount: 0,
    };
}
//# sourceMappingURL=questionnaire-edit.js.map